/* quiz.js */

async function addToCart(variantId, button) {
  // Ensure button exists
  if (!button) return;

  const originalText = button.textContent;
  button.textContent = 'Adding...';
  button.disabled = true;

  try {

    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1
        }]
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error('Failed to add to cart');
    }

    const data = await response.json();

    button.textContent = 'Added!';

    // Close quiz modal if open
    const quizModal = document.getElementById('quizModal');
    if (quizModal) {
      quizModal.classList.remove('active');
      document.body.style.overflow = '';
    }

    // Try to open cart drawer (else redirect)
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.open();
    } else {
      window.location.href = window.Shopify.routes.root + 'cart';
    }

  } catch (error) {
    button.textContent = 'Error - Try Again';
  } finally {
    setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
    }, 2000);
  }
}

class QuizRecommendation {
  constructor() {
    this.productCache = null;
    this.METAFIELD_MAP = {
      fragrance_type: { namespace: 'custom', key: 'fragrance_type' },
      occasions: { namespace: 'custom', key: 'occasions' },
      fragrance_notes: { namespace: 'custom', key: 'fragrance_notes' },
      projection: { namespace: 'custom', key: 'projection' },
      strength: { namespace: 'custom', key: 'strength' },
      gender: { namespace: 'custom', key: 'gender' },
      seasons: { namespace: 'custom', key: 'seasons' },
      longevity: { namespace: 'custom', key: 'longevity' }
    };
  }

  async fetchAllProducts() {
    if (this.productCache) return this.productCache;
    
    try {
      const response = await fetch('/pages/products-metafields');
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      // Filter out products that have no metafields
      this.productCache = data.products.filter(product => 
        product.metafields.some(m => m.value !== null)
      );

      return this.productCache;
    } catch (error) {
      throw error;
    }
  }

  calculateMatch(product, userAnswers) {
    let matchScore = 0;
    let totalCriteria = 0;

    const handleDimension = (dimensionKey) => {
      totalCriteria++;
      if (!userAnswers[dimensionKey]?.length) return;

      const { namespace, key } = this.METAFIELD_MAP[dimensionKey];
      const metafield = product.metafields?.find(
        m => m.namespace === namespace && m.key === key
      );

      if (!metafield?.value) return;

      let productValues = [];
      try {
        productValues = JSON.parse(metafield.value).map(v => v.trim().toLowerCase());
      } catch (err) {
        return;
      }

      const isMatch = userAnswers[dimensionKey].some(val =>
        productValues.includes(val.toLowerCase())
      );
      
      if (isMatch) matchScore++;
    };

    Object.keys(this.METAFIELD_MAP).forEach(key => {
      if (userAnswers[key]) handleDimension(key);
    });

    return totalCriteria === 0 ? 0 : (matchScore / totalCriteria) * 100;
  }

  async getRecommendations(userAnswers) {
    try {
      const products = await this.fetchAllProducts();

      const matched = products
        .map((p) => {
          // Print out the product's variants
          const matchPercentage = this.calculateMatch(p, userAnswers);
          return {
            ...p,
            matchPercentage
          };
        })
        .filter(p => p.matchPercentage > 0)
        .sort((a, b) => b.matchPercentage - a.matchPercentage)
        .slice(0, 3);

      // Now transform into the final data object we need for the quiz results
      const finalData = matched.map((p) => {
        // Safely retrieve first variant ID
        const variantId = p?.variants?.length ? p.variants[0].id : null;

        // Log it so you can see if it's undefined or valid

        return {
          id: variantId,
          title: p.title,
          handle: p.handle,
          price: parseInt(p.variants[0].price, 10),
          compareAtPrice: p.variants[0].compare_at_price 
            ? parseInt(p.variants[0].compare_at_price, 10) 
            : null,
          image: p.images[0]?.src || '',
          matchPercentage: Math.round(p.matchPercentage)
        };
      });

      return finalData;
    } catch (error) {
      throw error;
    }
  }
}

class FragranceQuiz {
  constructor() {
    this.currentStep = 1;
    this.totalSteps = 8;
    this.answers = {};
    this.recommendation = new QuizRecommendation();
    this.loadingOverlay = document.querySelector('.loading-overlay');
    
    this.bindEvents();
    this.showStep(1);
  }

  bindEvents() {
    // Navigation buttons
    document.querySelectorAll('.next-step').forEach(btn => {
      btn.addEventListener('click', () => this.nextStep());
    });

    document.querySelectorAll('.prev-step').forEach(btn => {
      btn.addEventListener('click', () => this.prevStep());
    });

    // Checkbox options
    document.querySelectorAll('.quiz-option input[type="checkbox"]').forEach(input => {
      input.addEventListener('change', (e) => {
        const option = e.target.closest('.quiz-option');
        if (option) {
          option.classList.toggle('selected', e.target.checked);
        }
      });
    });

    // Restart quiz
    const restartButton = document.querySelector('.restart-quiz');
    if (restartButton) {
      restartButton.addEventListener('click', () => this.resetQuiz());
    }
  }

  showStep(stepNumber) {
    document.querySelectorAll('.quiz-step').forEach(step => {
      step.style.display = 'none';
    });
    
    const targetStep = document.querySelector(`[data-step="${stepNumber}"]`);
    if (targetStep) {
      targetStep.style.display = 'block';
    }
  }

  collectStepAnswers() {
    const currentStepEl = document.querySelector(`[data-step="${this.currentStep}"]`);
    if (!currentStepEl) return;

    const checkboxes = currentStepEl.querySelectorAll('input[type="checkbox"]');
    if (!checkboxes.length) return;

    const fieldName = checkboxes[0].name;
    this.answers[fieldName] = Array.from(checkboxes)
      .filter(cb => cb.checked)
      .map(cb => cb.value);
  }

  nextStep() {
    this.collectStepAnswers();
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
      this.showStep(this.currentStep);
    } else {
      this.showResults();
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.showStep(this.currentStep);
    }
  }

  showLoading(show = true) {
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.toggle('active', show);
    }
  }

  formatMoney(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount / 100);
  }

  async showResults() {
    this.collectStepAnswers();
    
    const currentStepEl = document.querySelector(`[data-step="${this.currentStep}"]`);
    if (currentStepEl) {
      currentStepEl.style.display = 'none';
    }

    const loadingOverlay = document.querySelector('.loading-overlay');
    loadingOverlay.classList.add('active');

    const resultsEl = document.querySelector('.quiz-results');
    const productContainer = document.getElementById('results-container');

    if (!resultsEl || !productContainer) {
      return;
    }

    try {
      const recs = await this.recommendation.getRecommendations(this.answers);

      // Small delay for a smoother transition
      await new Promise(r => setTimeout(r, 800));

      if (!recs.length) {
        productContainer.innerHTML = `
          <div class="no-results">
            <h4>No matches found</h4>
            <p>Try different selections to find a better match.</p>
            <button onclick="location.reload()" class="button button--secondary">Start Over</button>
          </div>
        `;
      } else {
        productContainer.innerHTML = recs.map(p => `
          <div class="product-card">
            <div class="product-card__image-container">
              <img 
                class="product-card__image"
                src="${p.image}"
                alt="${p.title}"
                loading="lazy"
                width="400"
                height="400"
              >
              <div class="match-badge">${p.matchPercentage}% Match</div>
            </div>
            <div class="product-card__content">
              <h4 class="product-card__title">${p.title}</h4>
              <div class="product-card__price">
                ${
                  p.compareAtPrice > p.price 
                    ? `<s class="compare-at-price">${this.formatMoney(p.compareAtPrice)}</s>`
                    : ''
                }
                <span class="price">${this.formatMoney(p.price)}</span>
              </div>
              <div class="button-group">
                <a 
                  href="/products/${p.handle}" 
                  class="button button--primary"
                >
                  View Details
                </a>
                <button 
                  type="button"
                  class="button button--secondary"
                  onclick="addToCart(${p.id}, this)"
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        `).join('');
      }

      // Show results
      resultsEl.style.display = 'block';
      setTimeout(() => {
        resultsEl.classList.add('active');
      }, 10);

    } catch (error) {
      productContainer.innerHTML = `
        <div class="error-message">
          <p>Something went wrong. Please try again.</p>
          <button onclick="location.reload()" class="button button--secondary">Start Over</button>
        </div>
      `;
    } finally {
      loadingOverlay.classList.remove('active');
    }
  }

  resetQuiz() {
    this.currentStep = 1;
    this.answers = {};
    
    // Reset checkboxes
    document.querySelectorAll('.quiz-option input[type="checkbox"]').forEach(checkbox => {
      checkbox.checked = false;
      const option = checkbox.closest('.quiz-option');
      if (option) {
        option.classList.remove('selected');
      }
    });

    // Hide results and show first step
    const resultsEl = document.querySelector('.quiz-results');
    if (resultsEl) {
      resultsEl.style.display = 'none';
    }
    this.showStep(1);
  }
}

// Initialize quiz and modal
document.addEventListener('DOMContentLoaded', function() {
  const quizModal = document.getElementById('quizModal');
  if (!quizModal) return;

  let quiz = null;

  // Attach to link
  const quizLinks = document.querySelectorAll('a[href="#"]');
  quizLinks.forEach(link => {
    if (link.textContent.trim() === "Fragrance Quiz") {
      link.addEventListener('click', function(e) {
        e.preventDefault();
        quizModal.classList.add('active');
        document.body.style.overflow = 'hidden';
        
        if (!quiz) {
          quiz = new FragranceQuiz();
        }
      });
    }
  });

  // Close button
  const closeButton = quizModal.querySelector('.quiz-modal__close');
  if (closeButton) {
    closeButton.addEventListener('click', function() {
      quizModal.classList.remove('active');
      document.body.style.overflow = '';
    });
  }

  // Esc key closes quiz
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && quizModal.classList.contains('active')) {
      quizModal.classList.remove('active');
      document.body.style.overflow = '';
    }
  });

  // Expose globally
  window.addToCart = addToCart;
});